import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import XLSX from "xlsx";

import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import AddIcon from "@material-ui/icons/Add";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import SettingsIcon from "@material-ui/icons/Settings";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import Button from "designV2/components/Button";
import DialogShell from "designV2/components/DialogShell";
import EmptyState from "designV2/components/EmptyState";
import Field from "designV2/components/Field";
import FilterDropdown from "designV2/components/FilterDropdown";
import Pagination from "designV2/components/Pagination";
import SearchField from "designV2/components/SearchField";
import { TableSkeleton } from "designV2/components/Skeleton";
import TableToolbar from "designV2/components/TableToolbar";
import { graphqlUrl } from "utils/config";
import "./settingsList.css";

const UPLOAD_FILE = gql`
  mutation uploadFile($file: Upload!, $folderName: String!) {
    uploadFile(data: { file: $file, folderName: $folderName }) {
      url
    }
  }
`;

export const readableError = (error) => {
  if (error && error.graphQLErrors && error.graphQLErrors.length) {
    return error.graphQLErrors[0].message;
  }
  if (error && error.networkError) {
    return "We could not reach the server. Check your connection and try again.";
  }
  return "Something went wrong. Please try again.";
};

/** ENUM_LIKE_THIS reads badly in a table cell. */
export const humanize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^./, (character) => character.toUpperCase());

/** Turn a list of enum values into select options. */
export const enumOptions = (values) =>
  values.map((value) => ({ value, label: humanize(value) }));

const initialValues = (fields) => {
  const values = {};
  fields.forEach((field) => {
    values[field.name] = field.defaultValue !== undefined ? field.defaultValue : "";
  });
  return values;
};

/**
 * SettingsList - one paged list of small records, with add, edit and
 * delete.
 *
 * Every settings tab is this screen with different columns and a
 * different set of mutations, so they describe themselves through
 * props rather than repeating six hundred lines apiece.
 *
 * @param {Object} props
 * @param {string} props.title - Heading above the list
 * @param {string} props.description - What this list is for
 * @param {string} props.singular - Lower-case noun used throughout the copy
 * @param {Object} props.list - { query, key, variables } for the page of rows
 * @param {Object} props.count - { query, key, variables } for the total
 * @param {Object} props.create - { mutation, variables(values) }; omit to hide adding
 * @param {Object} props.update - { mutation, variables(values, row) }; omit to hide editing
 * @param {Object} props.remove - { mutation }; takes idSet, omit to hide deleting
 * @param {Object} props.importExcel - { mutation, templateRows, fileName }
 * @param {Array} props.columns - [{ key, label, width, render(row) }]
 * @param {Array} props.fields - [{ name, label, type, options, required, hint, ... }]
 * @param {Function} props.toValues - row -> form values, for editing
 * @param {Array} props.filters - [{ name, label, options }] server-side filters
 * @param {string} props.searchFilter - name of a String filter the search box drives
 * @param {Array} props.countFilters - filter names the count query understands;
 *   defaults to all of them. `getCarBrandsCount` takes none, so its total
 *   would otherwise keep counting rows the search has already excluded.
 * @param {boolean} props.standalone - Wrap in a page of its own, for a list
 *   that is a route rather than one tab of a group
 */
const SettingsList = ({
  title,
  description,
  singular = "item",
  standalone = false,
  list,
  count,
  create,
  update,
  remove,
  importExcel,
  columns,
  fields = [],
  toValues,
  filters = [],
  searchFilter,
  countFilters,
}) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterValues, setFilterValues] = useState({});

  const [selectedIds, setSelectedIds] = useState([]);
  const [editing, setEditing] = useState(null); // row being edited, or "new"
  const [values, setValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [deleteTargets, setDeleteTargets] = useState(null);
  const [uploadingField, setUploadingField] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(handler);
  }, [search]);

  // Any change to what is being asked for puts you back on page one;
  // staying on page 6 of a list that now has two pages showed nothing.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, pageSize, filterValues]);

  const activeFilters = useMemo(() => {
    const applied = {};
    Object.keys(filterValues).forEach((key) => {
      if (filterValues[key]) applied[key] = filterValues[key];
    });
    if (searchFilter && debouncedSearch) applied[searchFilter] = debouncedSearch;
    return applied;
  }, [filterValues, debouncedSearch, searchFilter]);

  const listVariables = Object.assign(
    { limit: pageSize, skip: page * pageSize },
    list.variables || {},
    activeFilters
  );

  const { loading, error, data, refetch } = useQuery(list.query, {
    variables: listVariables,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // Only hand the count query the filters it declares, and remember
  // whether that leaves the total describing something else.
  const countable = useMemo(() => {
    if (!countFilters) return activeFilters;
    const kept = {};
    countFilters.forEach((name) => {
      if (activeFilters[name] !== undefined) kept[name] = activeFilters[name];
    });
    return kept;
  }, [activeFilters, countFilters]);

  const totalIsExact =
    Object.keys(activeFilters).length === Object.keys(countable).length;

  const { data: countData, refetch: refetchCount } = useQuery(count.query, {
    variables: Object.assign({}, count.variables || {}, countable),
    fetchPolicy: "network-only",
  });

  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [runCreate] = useMutation((create && create.mutation) || UPLOAD_FILE);
  const [runUpdate] = useMutation((update && update.mutation) || UPLOAD_FILE);
  const [runRemove] = useMutation((remove && remove.mutation) || UPLOAD_FILE);
  const [runImport] = useMutation((importExcel && importExcel.mutation) || UPLOAD_FILE);

  const rows = (data && data[list.key]) || [];
  const totalCount = countData ? countData[count.key] : undefined;

  // The search box filters on the server when the query supports it and
  // on the page in hand when it does not, rather than doing nothing.
  const visibleRows = useMemo(() => {
    if (searchFilter || !debouncedSearch) return rows;
    const needle = debouncedSearch.toLowerCase();
    return rows.filter((row) =>
      columns.some((column) => {
        const raw = row[column.key];
        return typeof raw === "string" && raw.toLowerCase().indexOf(needle) !== -1;
      })
    );
  }, [rows, debouncedSearch, searchFilter, columns]);

  const reload = () => {
    refetch();
    refetchCount();
    setSelectedIds([]);
  };

  /* ---------------- selection ---------------- */

  const toggleRow = (id) =>
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );

  const toggleSelectAll = () =>
    setSelectedIds((current) =>
      current.length === visibleRows.length ? [] : visibleRows.map((row) => row._id)
    );

  const checkboxState =
    selectedIds.length === 0
      ? "unchecked"
      : selectedIds.length === visibleRows.length
      ? "checked"
      : "indeterminate";

  /* ---------------- editor ---------------- */

  const openCreate = () => {
    setEditing("new");
    setValues(initialValues(fields));
    setFieldErrors({});
    setFormError("");
  };

  const openEdit = (row) => {
    setEditing(row);
    setValues(toValues ? toValues(row) : initialValues(fields));
    setFieldErrors({});
    setFormError("");
  };

  const setValue = (name, value) =>
    setValues((current) => Object.assign({}, current, { [name]: value }));

  const validate = () => {
    const errors = {};
    fields.forEach((field) => {
      if (!field.required) return;
      const value = values[field.name];
      if (value === "" || value === null || value === undefined) {
        errors[field.name] = `${field.label} is required`;
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSave = () => {
    if (busy) return;
    setFormError("");
    if (!validate()) return;

    const isNew = editing === "new";
    const run = isNew ? runCreate : runUpdate;
    const variables = isNew
      ? create.variables(values)
      : update.variables(values, editing);

    setBusy(true);
    run({ variables })
      .then(() => {
        setBusy(false);
        setEditing(null);
        reload();
      })
      .catch((err) => {
        setBusy(false);
        setFormError(readableError(err));
      });
  };

  const onDelete = () => {
    if (busy || !deleteTargets) return;
    setBusy(true);
    runRemove({ variables: { idSet: deleteTargets.map((row) => row._id) } })
      .then(() => {
        setBusy(false);
        setDeleteTargets(null);
        reload();
      })
      .catch((err) => {
        setBusy(false);
        setDeleteTargets(null);
        setFormError(readableError(err));
      });
  };

  const onUploadImage = (field, file) => {
    if (!file) return;
    setUploadingField(field.name);
    uploadFile({ variables: { file, folderName: field.folderName || "settings" } })
      .then((res) => {
        setUploadingField("");
        setValue(field.name, res.data.uploadFile.url);
      })
      .catch((err) => {
        setUploadingField("");
        setFormError(readableError(err));
      });
  };

  /* ---------------- excel import ---------------- */

  const onDownloadTemplate = () => {
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      book,
      XLSX.utils.aoa_to_sheet(importExcel.templateRows),
      "Template"
    );
    XLSX.writeFile(book, importExcel.fileName);
  };

  const onImport = () => {
    if (busy || !importFile) return;
    setBusy(true);
    runImport({ variables: { excelFile: importFile } })
      .then(() => {
        setBusy(false);
        setImportOpen(false);
        setImportFile(null);
        reload();
      })
      .catch((err) => {
        setBusy(false);
        setFormError(readableError(err));
      });
  };

  /* ---------------- render ---------------- */

  const gridTemplate = [
    remove ? "36px" : "",
    ...columns.map((column) => column.width || "1fr"),
    update || remove ? "88px" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (error) return <ErrorPage />;

  const editorFields = (
    <div className="settingsForm">
      {formError && (
        <div className="dsFormMessage error" role="alert">
          {formError}
        </div>
      )}
      {fields.map((field) => (
        <Field
          key={field.name}
          label={field.label}
          htmlFor={`settings-${field.name}`}
          error={fieldErrors[field.name]}
          hint={field.hint}
          required={field.required}
        >
          {field.type === "textarea" ? (
            <textarea
              id={`settings-${field.name}`}
              className="dsInput settingsTextarea"
              value={values[field.name] || ""}
              onChange={(event) => setValue(field.name, event.target.value)}
            />
          ) : field.type === "select" ? (
            <select
              id={`settings-${field.name}`}
              className="dsInput"
              value={values[field.name] || ""}
              onChange={(event) => setValue(field.name, event.target.value)}
            >
              <option value="">Choose one</option>
              {(typeof field.options === "function" ? field.options() : field.options).map(
                (option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                )
              )}
            </select>
          ) : field.type === "color" ? (
            <div className="settingsColorRow">
              <input
                type="color"
                aria-label={`${field.label} swatch`}
                value={/^#[0-9a-fA-F]{6}$/.test(values[field.name] || "") ? values[field.name] : "#000000"}
                onChange={(event) => setValue(field.name, event.target.value)}
              />
              <input
                id={`settings-${field.name}`}
                className="dsInput"
                value={values[field.name] || ""}
                placeholder="#1a73e8"
                onChange={(event) => setValue(field.name, event.target.value)}
              />
            </div>
          ) : field.type === "image" ? (
            <div className="settingsImageRow">
              {values[field.name] && (
                <img src={`${graphqlUrl}${values[field.name]}`} alt={`${field.label} preview`} />
              )}
              <label className="settingsFilePicker">
                <CloudUploadIcon />
                {uploadingField === field.name
                  ? "Uploading"
                  : values[field.name]
                  ? "Replace image"
                  : "Choose image"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => onUploadImage(field, event.target.files[0])}
                />
              </label>
            </div>
          ) : (
            <input
              id={`settings-${field.name}`}
              className="dsInput"
              type={field.type === "number" ? "number" : "text"}
              step={field.step}
              min={field.min}
              placeholder={field.placeholder}
              value={values[field.name] === undefined ? "" : values[field.name]}
              onChange={(event) => setValue(field.name, event.target.value)}
            />
          )}
        </Field>
      ))}
    </div>
  );

  const body = (
    <div className="settingsSection">
      <div className="settingsSectionHead">
        <div>
          {standalone ? <h1>{title}</h1> : <h2>{title}</h2>}
          {description && <p>{description}</p>}
        </div>
        <div className="settingsSectionActions">
          {importExcel && (
            <Button
              variant="secondary"
              icon={<CloudUploadIcon fontSize="small" />}
              onClick={() => setImportOpen(true)}
            >
              Import
            </Button>
          )}
          {create && (
            <Button
              variant="primary"
              icon={<AddIcon fontSize="small" />}
              onClick={openCreate}
            >
              Add {singular}
            </Button>
          )}
        </div>
      </div>

      {(filters.length > 0 || columns.some((column) => column.key)) && (
        <div className="settingsFilters">
          <SearchField
            placeholder={`Search ${singular}s`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {filters.map((filter) => (
            <FilterDropdown
              key={filter.name}
              label={
                filterValues[filter.name]
                  ? `${filter.label}: ${humanize(filterValues[filter.name])}`
                  : filter.label
              }
              options={[{ value: "", label: `All ${filter.label.toLowerCase()}` }].concat(
                filter.options
              )}
              value={filterValues[filter.name] || ""}
              active={Boolean(filterValues[filter.name])}
              onChange={(event) =>
                setFilterValues((current) =>
                  Object.assign({}, current, { [filter.name]: event.target.value })
                )
              }
            />
          ))}
        </div>
      )}

      {remove && (
        <TableToolbar
          selectedCount={selectedIds.length}
          checkboxState={checkboxState}
          onCheckboxChange={toggleSelectAll}
          bulkActions={
            <Button
              variant="dangerQuiet"
              size="sm"
              icon={<DeleteIcon fontSize="small" />}
              onClick={() =>
                setDeleteTargets(rows.filter((row) => selectedIds.includes(row._id)))
              }
            >
              Delete selected
            </Button>
          }
          rightActions={
            <IconButton className="tableTopIconButton" onClick={reload} title="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
          }
        />
      )}

      {loading ? (
        <TableSkeleton rows={pageSize > 10 ? 10 : pageSize} />
      ) : visibleRows.length === 0 ? (
        <EmptyState
          icon={<SettingsIcon />}
          title={debouncedSearch ? `No ${singular} matches that` : `No ${singular}s yet`}
          message={
            debouncedSearch
              ? "Try a different search."
              : create
              ? `Add the first one to get started.`
              : "Nothing has been set up here."
          }
          action={
            create && !debouncedSearch ? (
              <Button variant="primary" icon={<AddIcon fontSize="small" />} onClick={openCreate}>
                Add {singular}
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="tableResponsive">
          <div className="table">
            <div className="tableHeader" style={{ gridTemplateColumns: gridTemplate }}>
              {remove && <div className="tableHeaderCell tableHeaderCheckbox" />}
              {columns.map((column) => (
                <div key={column.key || column.label} className="tableHeaderCell">
                  {column.label}
                </div>
              ))}
              {(update || remove) && (
                <div className="tableHeaderCell tableHeaderAction">Actions</div>
              )}
            </div>

            {visibleRows.map((row) => (
              <div key={row._id} className="row" style={{ gridTemplateColumns: gridTemplate }}>
                {remove && (
                  <div>
                    <Checkbox
                      size="small"
                      checked={selectedIds.includes(row._id)}
                      onChange={() => toggleRow(row._id)}
                      inputProps={{ "aria-label": `Select ${row._id}` }}
                    />
                  </div>
                )}
                {columns.map((column) => (
                  <div key={column.key || column.label} data-label={column.label}>
                    {column.render ? column.render(row) : row[column.key]}
                  </div>
                ))}
                {(update || remove) && (
                  <div className="settingsRowActions" data-label="Actions">
                    {update && (
                      <IconButton size="small" title="Edit" onClick={() => openEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    )}
                    {remove && (
                      <IconButton
                        size="small"
                        title="Delete"
                        onClick={() => setDeleteTargets([row])}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!totalIsExact && visibleRows.length > 0 && (
        <p className="settingsImportHint">
          Showing {visibleRows.length} {visibleRows.length === 1 ? "match" : "matches"}. Narrow
          the search to see fewer.
        </p>
      )}

      {totalIsExact && totalCount !== undefined && totalCount > 0 && (
        <Pagination
          currentPage={page}
          pageSize={pageSize}
          totalCount={totalCount}
          pageSizeOptions={[10, 20, 50]}
          onPageChange={(next) => setPage(next)}
          onPageSizeChange={(size) => setPageSize(parseInt(size, 10))}
        />
      )}

      <DialogShell
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing === "new" ? `Add ${singular}` : `Edit ${singular}`}
        size="md"
        actions={[
          { label: "Cancel", onClick: () => setEditing(null), variant: "secondary" },
          {
            label: editing === "new" ? "Add" : "Save",
            onClick: onSave,
            variant: "primary",
            loading: busy,
          },
        ]}
      >
        {editorFields}
      </DialogShell>

      <DialogShell
        open={Boolean(deleteTargets)}
        onClose={() => setDeleteTargets(null)}
        title={
          deleteTargets && deleteTargets.length > 1
            ? `Delete ${deleteTargets.length} ${singular}s?`
            : `Delete this ${singular}?`
        }
        type="error"
        size="sm"
        message={
          deleteTargets && deleteTargets.length === 1
            ? `"${
                deleteTargets[0][columns[0].key] || deleteTargets[0]._id
              }" will be removed. This cannot be undone.`
            : "This cannot be undone."
        }
        actions={[
          { label: "Cancel", onClick: () => setDeleteTargets(null), variant: "secondary" },
          { label: "Delete", onClick: onDelete, variant: "danger", loading: busy },
        ]}
      />

      {importExcel && (
        <DialogShell
          open={importOpen}
          onClose={() => setImportOpen(false)}
          title={`Import ${singular}s from a spreadsheet`}
          size="sm"
          actions={[
            { label: "Cancel", onClick: () => setImportOpen(false), variant: "secondary" },
            {
              label: "Import",
              onClick: onImport,
              variant: "primary",
              disabled: !importFile,
              loading: busy,
            },
          ]}
        >
          <div className="settingsForm">
            <p className="settingsImportHint">
              Download the template, fill in one row per {singular}, then upload it here.
            </p>
            <Button
              variant="secondary"
              icon={<GetAppIcon fontSize="small" />}
              onClick={onDownloadTemplate}
            >
              Download template
            </Button>
            <label className="settingsFilePicker">
              <CloudUploadIcon />
              {importFile ? importFile.name : "Choose spreadsheet"}
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(event) => setImportFile(event.target.files[0])}
              />
            </label>
          </div>
        </DialogShell>
      )}
    </div>
  );

  if (!standalone) return body;

  return (
    <div className="page settingsPage">
      <main className="main">{body}</main>
    </div>
  );
};

export default SettingsList;
